import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Warning, Check } from "@phosphor-icons/react";

/**
 * ContactsCsvImportDialog — Iter 85.3
 *
 * A partner pastes rows in `name, title, email, phone, role` order (with
 * or without a header). We preview parsed rows, let them fix or drop bad
 * lines, then POST /clients/{cid}/contacts/bulk to create them all in
 * one shot. No file upload — paste is faster for the org-chart use case.
 *
 * Role tokens accepted (case-insensitive, whitespace/hyphen normalized):
 *   primary poc, executive sponsor, day to day, billing admin,
 *   board governance, other
 * Unknown role tokens fall back to `other` silently.
 */
const KNOWN_ROLES = {
  "primary_poc": "primary_poc",
  "primary poc": "primary_poc",
  "primary": "primary_poc",
  "executive_sponsor": "executive_sponsor",
  "executive sponsor": "executive_sponsor",
  "sponsor": "executive_sponsor",
  "day_to_day": "day_to_day",
  "day to day": "day_to_day",
  "day-to-day": "day_to_day",
  "operational": "day_to_day",
  "billing_admin": "billing_admin",
  "billing admin": "billing_admin",
  "billing": "billing_admin",
  "admin": "billing_admin",
  "board_governance": "board_governance",
  "board governance": "board_governance",
  "board": "board_governance",
  "governance": "board_governance",
  "other": "other",
};

// Naive CSV split — respects double-quote wrapping so `"Doe, Jane", CEO`
// parses cleanly as ['Doe, Jane', 'CEO']. Good enough for a paste flow.
function splitCsvLine(line) {
  const out = [];
  let cur = "";
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') { inQ = !inQ; continue; }
    if (ch === "," && !inQ) { out.push(cur); cur = ""; continue; }
    cur += ch;
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

function parseCsv(text) {
  const raw = (text || "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!raw.length) return [];
  let header = null;
  const first = splitCsvLine(raw[0]).map((s) => s.toLowerCase());
  if (first.some((h) => h === "name")) {
    header = first;
    raw.shift();
  }
  const cols = header || ["name", "title", "email", "phone", "role"];
  return raw.map((line, i) => {
    const parts = splitCsvLine(line);
    const row = { _row: i + (header ? 2 : 1) };
    cols.forEach((k, idx) => { row[k] = parts[idx] || ""; });
    if (row.role) {
      const norm = row.role.toLowerCase().replace(/[-]+/g, " ").replace(/\s+/g, " ").trim();
      row.role = KNOWN_ROLES[norm] || KNOWN_ROLES[norm.replace(/\s+/g, "_")] || "other";
    } else {
      row.role = "other";
    }
    row._valid = !!(row.name || "").trim();
    return row;
  });
}

// Iter 85.6 · Duplicate Detector helper. Given the client's current
// stakeholders and a parsed row, return the id of any existing contact
// whose email matches exact-lowercase. Returns null when no conflict.
function findEmailDupe(row, existing) {
  const em = (row.email || "").trim().toLowerCase();
  if (!em) return null;
  for (const s of existing || []) {
    if (!s || !s.id) continue;
    if ((s.email || "").trim().toLowerCase() === em) return s;
  }
  return null;
}

export default function ContactsCsvImportDialog({ clientId, open, onOpenChange, onImported, existingContacts = [] }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const parsed = useMemo(() => parseCsv(text), [text]);
  const validRows = parsed.filter((r) => r._valid);
  const invalidRows = parsed.filter((r) => !r._valid);
  // Iter 85.6 · Duplicate Detector — surface any row whose email matches
  // an existing contact so the partner can drop or merge instead of
  // silently creating a duplicate.
  const dupeRows = parsed.map((r) => ({ ...r, _dupe: findEmailDupe(r, existingContacts) })).filter((r) => r._valid && r._dupe);

  const doImport = async () => {
    if (!validRows.length) return toast.error("Nothing valid to import");
    setSaving(true);
    try {
      const payload = {
        contacts: validRows.map((r) => ({
          name: r.name,
          title: r.title || "",
          email: r.email || "",
          phone: r.phone || "",
          role: r.role,
        })),
      };
      const { data } = await api.post(`/clients/${clientId}/contacts/bulk`, payload);
      toast.success(`Imported ${data.created.length} contact${data.created.length === 1 ? "" : "s"}`);
      onImported?.(data.client);
      setText("");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="contacts-csv-dialog">
        <DialogHeader>
          <DialogTitle>Import contacts from CSV</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Paste rows in the order <span className="font-mono">name, title, email, phone, role</span>. A header row is optional. Blank names are skipped.
          </DialogDescription>
        </DialogHeader>

        <Textarea
          rows={8}
          placeholder={`Jane Doe, CFO, jane@acme.io, 555-1212, executive sponsor\nJohn Smith, Head of Ops, john@acme.io, , day to day`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="rounded-none bg-secondary/40 font-mono text-xs"
          data-testid="contacts-csv-textarea"
        />

        {parsed.length > 0 && (
          <section className="border border-border" data-testid="contacts-csv-preview">
            <div className="border-b border-border px-3 py-2 text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-3">
              <span className="text-primary">Preview · {validRows.length} valid</span>
              {invalidRows.length > 0 && (
                <span className="inline-flex items-center gap-1 text-destructive">
                  <Warning size={11} weight="fill" /> {invalidRows.length} skipped
                </span>
              )}
              {dupeRows.length > 0 && (
                <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400" data-testid="contacts-csv-dupe-summary">
                  <Warning size={11} weight="fill" /> {dupeRows.length} match{dupeRows.length === 1 ? "es" : "es"} an existing contact
                </span>
              )}
            </div>
            <ul className="max-h-64 overflow-y-auto divide-y divide-border">
              {parsed.map((r) => {
                const dupe = r._valid ? findEmailDupe(r, existingContacts) : null;
                return (
                  <li key={r._row} className={`px-3 py-2 text-xs ${r._valid ? "" : "opacity-40"}`} data-testid={`contacts-csv-row-${r._row}`}>
                    <div className="flex items-center gap-2">
                      {r._valid ? <Check size={11} weight="bold" className="text-emerald-500" /> : <Warning size={11} weight="fill" className="text-destructive" />}
                      <span className="font-medium">{r.name || <em>(missing name — skipped)</em>}</span>
                      {r.title && <span className="text-muted-foreground">· {r.title}</span>}
                      {dupe && (
                        <span
                          className="ml-auto text-[0.5625rem] font-mono uppercase tracking-widest text-amber-600 dark:text-amber-400 border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5"
                          title={`Existing contact "${dupe.name}" already uses this email — importing will create a duplicate you may want to merge afterwards.`}
                          data-testid={`contacts-csv-row-dupe-${r._row}`}
                        >
                          Duplicate email → {dupe.name}
                        </span>
                      )}
                      {!dupe && r.role && (
                        <span className="ml-auto text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">{r.role}</span>
                      )}
                    </div>
                    {(r.email || r.phone) && (
                      <div className="mt-0.5 text-[0.625rem] font-mono text-muted-foreground">
                        {r.email && <>{r.email} </>}
                        {r.phone && <>· {r.phone}</>}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={doImport}
            disabled={saving || validRows.length === 0}
            className="rounded-none bg-primary text-primary-foreground"
            data-testid="contacts-csv-import-btn"
          >
            {saving ? "Importing…" : `Import ${validRows.length} contact${validRows.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
